import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import {Roles} from "@evennt-tech/api-v2-core";
import {selectUser} from "store/auth/selectors";
import {extractOrigin} from "tools/url/extractDomain";
import {useMyVerifiedDomains} from "hooks/verifiedDomain/useMyVerifiedDomains";
import {useCheckVerifiedDomains} from "hooks/verifiedDomain/useCheckVerifiedDomains";
import {IFRAME_CLASS, IFRAME_WRAP_CLASS} from "../blots/IframeBlot";

export type OverlayStatus =
  | CheckVerifiedDomainStatus.PENDING
  | CheckVerifiedDomainStatus.REJECTED;

export type OverlayEntry = {
  wrap: HTMLElement;
  status: OverlayStatus;
};

type Args = {
  rootEditorElement: HTMLElement | null;
  contentVersion: number;
};

const resolveStatus = (
  src: string,
  origin: string | null,
  checkResult: Map<string, CheckVerifiedDomainStatus>,
  myDomains: ReturnType<typeof useMyVerifiedDomains>[0]
): OverlayStatus | null => {
  const srcStatus = src ? checkResult.get(src.toLowerCase()) : undefined;
  const originStatus = origin
    ? checkResult.get(origin.toLowerCase())
    : undefined;

  if (
    srcStatus === CheckVerifiedDomainStatus.APPROVED ||
    originStatus === CheckVerifiedDomainStatus.APPROVED
  ) {
    return null;
  }
  const ownRecord = myDomains.find((d) => {
    const u = (d.url || "").toLowerCase();
    return (
      u === src.toLowerCase() ||
      (origin && u === origin.toLowerCase()) ||
      extractOrigin(d.url) === origin
    );
  });
  if (ownRecord?.isApproved === true) return null;
  if (
    srcStatus === CheckVerifiedDomainStatus.REJECTED ||
    originStatus === CheckVerifiedDomainStatus.REJECTED
  )
    return CheckVerifiedDomainStatus.REJECTED;
  if (ownRecord?.isApproved === false)
    return CheckVerifiedDomainStatus.REJECTED;
  return CheckVerifiedDomainStatus.PENDING;
};

const restoreIframeVisibility = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLIFrameElement>(`iframe.${IFRAME_CLASS}`)
    .forEach((node) => {
      node.style.removeProperty("visibility");
      if (!node.getAttribute("src")) {
        const wrap = node.closest<HTMLElement>(`.${IFRAME_WRAP_CLASS}`);
        const dataSrc = wrap?.getAttribute("data-src");
        if (dataSrc) {
          node.setAttribute("src", dataSrc);
        }
      }
    });
};

const blankIframe = (iframe: HTMLIFrameElement) => {
  iframe.style.visibility = "hidden";
  iframe.removeAttribute("src");
};

export const useIframeStatusOverlay = ({
  rootEditorElement,
  contentVersion,
}: Args): OverlayEntry[] => {
  const {role} = useSelector(selectUser);
  const [domains, setDomains] = useState<string[]>([]);
  const [entries, setEntries] = useState<OverlayEntry[]>([]);
  const [myDomains] = useMyVerifiedDomains(role === Roles.ORGANISER);
  const {result: checkResult, isReady} = useCheckVerifiedDomains(domains);

  useEffect(() => {
    if (!rootEditorElement) {
      setDomains([]);
      return;
    }
    const found = new Set<string>();
    rootEditorElement
      .querySelectorAll<HTMLElement>(`.${IFRAME_WRAP_CLASS}`)
      .forEach((wrap) => {
        const src = (wrap.getAttribute("data-src") || "").trim();
        if (src) found.add(src);
        const origin = extractOrigin(src);
        if (origin) found.add(origin);
      });
    setDomains(Array.from(found));
  }, [rootEditorElement, contentVersion]);

  useEffect(() => {
    if (!rootEditorElement) {
      setEntries([]);
      return;
    }
    if (!isReady) return;

    const wrapNodes = rootEditorElement.querySelectorAll<HTMLElement>(
      `.${IFRAME_WRAP_CLASS}`
    );
    if (wrapNodes.length > 0 && domains.length === 0) return;
    restoreIframeVisibility(rootEditorElement);
    const next: OverlayEntry[] = [];
    wrapNodes.forEach((wrap) => {
      const iframe = wrap.querySelector<HTMLIFrameElement>(
        `iframe.${IFRAME_CLASS}`
      );
      if (!iframe) return;

      const src = (wrap.getAttribute("data-src") || "").trim();
      const origin = extractOrigin(src);
      const status = resolveStatus(src, origin, checkResult, myDomains);
      if (!status) return;
      blankIframe(iframe);
      next.push({wrap, status});
    });
    setEntries(next);
  }, [
    rootEditorElement,
    checkResult,
    isReady,
    myDomains,
    contentVersion,
    domains,
  ]);

  useEffect(() => {
    return () => {
      if (rootEditorElement) restoreIframeVisibility(rootEditorElement);
    };
  }, [rootEditorElement]);

  return entries;
};
